import { useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal, ModalFooter } from './Modal';

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, code: string | undefined, quantity: number) => void;
  isPending: boolean;
}

export function AddItemModal({
  isOpen,
  onClose,
  onSubmit,
  isPending,
}: AddItemModalProps) {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [quantity, setQuantity] = useState(1);

  const handleSubmit = () => {
    if (!name.trim()) return;
    const cleanCode = code.trim().replace(/\D/g, '') || undefined;
    onSubmit(name.trim(), cleanCode, quantity);
  };

  const handleClose = () => {
    setName('');
    setCode('');
    setQuantity(1);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('checklists.addItem', 'Add Item')}
      icon={Plus}
      iconColor="primary"
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('checklists.productNameRequired', 'Product Name *')}
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder={t('checklists.productNamePlaceholder', 'e.g., KALLAX Shelf')}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('checklists.productCodeOptional', 'Product Code (optional)')}
          </label>
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder={t('checklists.productCodePlaceholder', 'e.g., 402.998.85')}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow font-mono"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('checklists.quantity', 'Quantity')}
          </label>
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow"
          />
        </div>

        <ModalFooter>
          <button
            onClick={handleClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-xl transition-colors"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || !name.trim()}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('checklists.add', 'Add')}
          </button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
